import { useMemo } from "react";
import { useAchievements } from "./useAchievements";
import { useMissions } from "./useMissions";
import { useFriends } from "./useFriends";

export interface ProfileNotification {
  category: "rewards" | "friends" | "missions";
  count: number;
}

export function useProfileNotifications() {
  const { claimableAchievements } = useAchievements();
  const { dailyMissions, weeklyMissions } = useMissions();
  const { pendingReceived } = useFriends();

  const claimableMissionCount = useMemo(
    () => [...dailyMissions, ...weeklyMissions].filter((m) => m.status === "completed").length,
    [dailyMissions, weeklyMissions]
  );

  const rewardCount = useMemo(
    () => claimableAchievements.length + claimableMissionCount,
    [claimableAchievements, claimableMissionCount]
  );

  const friendCount = useMemo(() => pendingReceived?.length ?? 0, [pendingReceived]);

  const notifications = useMemo<ProfileNotification[]>(() => {
    const items: ProfileNotification[] = [];
    if (rewardCount > 0) items.push({ category: "rewards", count: rewardCount });
    if (friendCount > 0) items.push({ category: "friends", count: friendCount });
    return items;
  }, [rewardCount, friendCount]);

  const totalCount = rewardCount + friendCount;

  return { notifications, totalCount, rewardCount, friendCount };
}
